/**
 * AIAssistant — Asistente de Marketing Inteligente potenciado por Google Gemini.
 * 
 * Pestañas:
 * 1. Generador de Copys (Instagram, TikTok, LinkedIn, Facebook)
 * 2. Planificador de Ideas de Contenido
 * 3. Consultor Estratégico & Diagnóstico
 */

import { useState, useEffect } from 'react';
import {
  Sparkles,
  Lightbulb,
  BarChart3,
  Copy,
  Check,
  Loader2,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import type { Client } from '@/types';

export function AIAssistant() {
  const { currentPeriod } = useAppStore();
  const [clients, setClients] = useState<Client[]>([]);
  const [activeTab, setActiveTab] = useState<'copy' | 'ideas' | 'diagnosis'>('copy');

  // Estado: Generador de Copys
  const [copyClient, setCopyClient] = useState('');
  const [copyPlatform, setCopyPlatform] = useState('instagram');
  const [copyFormat, setCopyFormat] = useState('reel');
  const [copyTone, setCopyTone] = useState('cercano');
  const [copyTopic, setCopyTopic] = useState('');
  const [copyContext, setCopyContext] = useState('');
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [generatedCopy, setGeneratedCopy] = useState('');
  const [copied, setCopied] = useState(false);

  // Estado: Ideas de Contenido
  const [ideasClient, setIdeasClient] = useState('');
  const [ideasNiche, setIdeasNiche] = useState('');
  const [ideasMonth, setIdeasMonth] = useState(currentPeriod);
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [generatedIdeas, setGeneratedIdeas] = useState('');

  // Estado: Consultor Estratégico
  const [diagClient, setDiagClient] = useState('');
  const [diagNotes, setDiagNotes] = useState('');
  const [generatingDiag, setGeneratingDiag] = useState(false);
  const [generatedDiag, setGeneratedDiag] = useState('');

  useEffect(() => {
    api.getClients().then((res) => setClients(res.clients as Client[]));
  }, []);

  // Generar Copy
  const handleGenerateCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!copyTopic) return;

    setGeneratingCopy(true);
    setGeneratedCopy('');
    try {
      const clientObj = clients.find((c) => c.id === copyClient);
      const res = await api.generateCopy({
        clientName: clientObj?.name || 'Marca Personal',
        businessType: clientObj?.business_name || 'Negocio / Servicios',
        platform: copyPlatform,
        contentType: copyFormat,
        topic: copyTopic,
        tone: copyTone,
        additionalContext: copyContext || undefined,
      });
      setGeneratedCopy(res.copy);
    } catch (err: any) {
      console.error('Error generando copy:', err);
      setGeneratedCopy(`Error al conectar con Gemini: ${err.message || 'Verifica tu API key en Cloudflare Workers.'}`);
    } finally {
      setGeneratingCopy(false);
    }
  };

  // Generar Ideas
  const handleGenerateIdeas = async (e: React.FormEvent) => {
    e.preventDefault();
    const clientObj = clients.find((c) => c.id === ideasClient);
    const clientName = clientObj?.name || 'Marca';
    const businessType = ideasNiche || clientObj?.business_name || 'Servicios y Productos';

    setGeneratingIdeas(true);
    setGeneratedIdeas('');
    try {
      const res = await api.suggestContent({
        clientName,
        businessType,
        month: ideasMonth,
        currentTopics: [],
      });
      setGeneratedIdeas(res.suggestions);
    } catch (err: any) {
      console.error('Error generando ideas:', err);
      setGeneratedIdeas(`Error al conectar con Gemini: ${err.message || 'Error desconocido'}`);
    } finally {
      setGeneratingIdeas(false);
    }
  };

  // Diagnóstico
  const handleGenerateDiagnosis = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneratingDiag(true);
    setGeneratedDiag('');
    try {
      let metricsToAnalyze: any[] = [];
      const clientObj = clients.find((c) => c.id === diagClient);

      if (diagClient) {
        try {
          const res = await api.getMetrics(diagClient);
          if (res.metrics && res.metrics.length > 0) {
            metricsToAnalyze = res.metrics.map((m: any) => ({
              ...m,
              clientName: clientObj?.name || 'Cliente',
              notes: diagNotes || undefined,
            }));
          }
        } catch (fetchErr) {
          console.warn('No se pudieron obtener métricas previas del cliente:', fetchErr);
        }
      }

      if (metricsToAnalyze.length === 0) {
        metricsToAnalyze = [
          {
            clientName: clientObj?.name || 'Marca / Negocio',
            platform: 'Instagram',
            followers: 4500,
            reach: 12500,
            engagement_rate: 4.8,
            notes: diagNotes || 'Diagnóstico y plan de optimización de redes sociales',
          },
        ];
      }

      const res = await api.analyzeMetrics(metricsToAnalyze);
      setGeneratedDiag(res.analysis);
    } catch (err: any) {
      console.error('Error generando análisis:', err);
      setGeneratedDiag(`Error al generar análisis: ${err.message || 'Error desconocido'}`);
    } finally {
      setGeneratingDiag(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="text-primary animate-pulse" />
            Asistente IA de Marketing
          </h1>
          <p className="text-muted-foreground text-sm">
            Potenciado por Google Gemini Flash. Diseñado especialmente para agilizar tu flujo diario.
          </p>
        </div>
      </div>

      {/* ─── Selector de Herramienta / Pestañas ─── */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('copy')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'copy'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Zap size={16} />
          Generador de Copys
        </button>
        <button
          onClick={() => setActiveTab('ideas')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'ideas'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Lightbulb size={16} />
          Planificador de Ideas
        </button>
        <button
          onClick={() => setActiveTab('diagnosis')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'diagnosis'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <BarChart3 size={16} />
          Consultoría & Diagnóstico
        </button>
      </div>

      {/* ─── TAB 1: GENERADOR DE COPYS ─── */}
      {activeTab === 'copy' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configuración del Copy</CardTitle>
              <CardDescription>
                Define la intención, formato y cliente para crear textos de alto impacto.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerateCopy} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Cliente / Marca
                  </label>
                  <select
                    value={copyClient}
                    onChange={(e) => setCopyClient(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Marca Personal / General</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">
                      Plataforma
                    </label>
                    <select
                      value={copyPlatform}
                      onChange={(e) => setCopyPlatform(e.target.value)}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="facebook">Facebook</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">
                      Formato
                    </label>
                    <select
                      value={copyFormat}
                      onChange={(e) => setCopyFormat(e.target.value)}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="reel">Reel / Video Corto</option>
                      <option value="carousel">Carrusel Educativo</option>
                      <option value="post">Post Estándar</option>
                      <option value="story">Secuencia de Stories</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Tono de Comunicación
                  </label>
                  <select
                    value={copyTone}
                    onChange={(e) => setCopyTone(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="cercano">Cercano y Empático</option>
                    <option value="profesional">Profesional y de Autoridad</option>
                    <option value="divertido">Divertido e Informal con Humor</option>
                    <option value="urgencia">Directo a la Venta / Urgencia</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Tema principal o Gancho
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: 3 errores comunes al cuidar la piel en invierno..."
                    value={copyTopic}
                    onChange={(e) => setCopyTopic(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Contexto o Llamado a la Acción (Opcional)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Ej: Dejar comentario 'QUIERO' para enviar enlace por MD. 20% descuento."
                    value={copyContext}
                    onChange={(e) => setCopyContext(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <Button type="submit" disabled={generatingCopy} className="w-full gap-2">
                  {generatingCopy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Gemini está creando tu copy...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Generar Copy con IA
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Resultado */}
          <Card className="flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Resultado del Copy</CardTitle>
                <CardDescription>Opciones generadas listas para publicar</CardDescription>
              </div>
              {generatedCopy && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedCopy)}
                  className="gap-1.5"
                >
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex-1">
              {generatingCopy ? (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm">Escribiendo ganchos y llamados a la acción...</p>
                </div>
              ) : generatedCopy ? (
                <div className="rounded-lg bg-muted/40 p-4 border text-sm whitespace-pre-line leading-relaxed font-sans max-h-[500px] overflow-y-auto">
                  {generatedCopy}
                </div>
              ) : (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground text-center p-6">
                  <Sparkles className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <p className="text-sm">Completa el formulario y pulsa Generar para recibir 3 variantes de copy listas para tus clientes.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── TAB 2: PLANIFICADOR DE IDEAS ─── */}
      {activeTab === 'ideas' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Estrategia Mensual de Contenidos</CardTitle>
              <CardDescription>
                Genera pilares de contenido y temáticas de alto engagement para el mes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerateIdeas} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Cliente
                  </label>
                  <select
                    value={ideasClient}
                    onChange={(e) => setIdeasClient(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Nicho / Tipo de Negocio
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Centro de Estética, Abogada Familiar, Restaurante Vegano..."
                    value={ideasNiche}
                    onChange={(e) => setIdeasNiche(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Mes objetivo
                  </label>
                  <input
                    type="month"
                    value={ideasMonth}
                    onChange={(e) => setIdeasMonth(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <Button type="submit" disabled={generatingIdeas} className="w-full gap-2">
                  {generatingIdeas ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Planificando estrategia...
                    </>
                  ) : (
                    <>
                      <Lightbulb size={16} />
                      Generar Banco de Ideas
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Resultado Ideas */}
          <Card className="flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Ideas Propuestas</CardTitle>
                <CardDescription>Pilares de contenido sugeridos por Gemini</CardDescription>
              </div>
              {generatedIdeas && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedIdeas)}
                  className="gap-1.5"
                >
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex-1">
              {generatingIdeas ? (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm">Pensando ideas innovadoras para tu nicho...</p>
                </div>
              ) : generatedIdeas ? (
                <div className="rounded-lg bg-muted/40 p-4 border text-sm whitespace-pre-line leading-relaxed font-sans max-h-[500px] overflow-y-auto">
                  {generatedIdeas}
                </div>
              ) : (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground text-center p-6">
                  <Lightbulb className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <p className="text-sm">Selecciona tu cliente y mes para obtener 10 ideas de contenido categorizadas por objetivo.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── TAB 3: CONSULTORÍA & DIAGNÓSTICO ─── */}
      {activeTab === 'diagnosis' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Diagnóstico de Desempeño</CardTitle>
              <CardDescription>
                Pídele a Gemini un análisis crítico para el reporte mensual del cliente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerateDiagnosis} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Cliente a diagnosticar
                  </label>
                  <select
                    value={diagClient}
                    onChange={(e) => setDiagClient(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Observaciones o dudas sobre el rendimiento
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Ej: Las vistas en reels subieron pero los mensajes de WhatsApp bajaron. La audiencia parece no responder al último carrusel de precios..."
                    value={diagNotes}
                    onChange={(e) => setDiagNotes(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <Button type="submit" disabled={generatingDiag} className="w-full gap-2">
                  {generatingDiag ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analizando datos...
                    </>
                  ) : (
                    <>
                      <BarChart3 size={16} />
                      Generar Diagnóstico Estratégico
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Resultado Diagnóstico */}
          <Card className="flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Informe Estratégico</CardTitle>
                <CardDescription>Recomendaciones accionables de crecimiento</CardDescription>
              </div>
              {generatedDiag && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedDiag)}
                  className="gap-1.5"
                >
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex-1">
              {generatingDiag ? (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm">Analizando métricas y formulando plan de acción...</p>
                </div>
              ) : generatedDiag ? (
                <div className="rounded-lg bg-muted/40 p-4 border text-sm whitespace-pre-line leading-relaxed font-sans max-h-[500px] overflow-y-auto">
                  {generatedDiag}
                </div>
              ) : (
                <div className="flex h-64 flex-col items-center justify-center text-muted-foreground text-center p-6">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <p className="text-sm">Escribe observaciones sobre el cliente para que Gemini te brinde sugerencias concretas de optimización.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
